import type {
	OrderAddress,
	Period,
} from '@/modules/companyNeed/components/modals/createOrder/types';
import type { NeedRecruitmentCardAlerts } from '@/modules/companyNeed/types/recruitment';
import type {
	MissionAction,
	MissionPeriod,
	MissionState,
	MissionStatus,
} from '@/modules/companyNeed/views/details/missions/types';
import type { CancelReason, WorkerMissionStatus } from '@/types/mission';
import type { Remuneration } from '@/types/remuneration';

export interface MissionModalData {
	id: string;
	besoinId: string;
	companyId: string;
	jobId: string;
	status: MissionStatus;
	name: string;
	totalPositions: number;
	periods: Array<MissionPeriod>;
	address: OrderAddress;
	contractType: string;

	remuneration?: Remuneration;
	showRemuneration?: boolean;
	hiringReason?: string;
	explanation?: string;
	description?: string;
	note?: string;

	states: MissionState[];
	availableActions: MissionAction[];
	workersSelection: WorkersSelectionModal[];

	createdBy: {
		firstname: string;
		lastname: string;
	};
	createdAt: number; // timestamp
}

export interface WorkersSelectionModal {
	workerStatus: WorkerMissionStatus;
	workers: MissionWorker[];
}

export interface MissionWorker {
	id: string;
	firstname: string;
	lastname: string;
	phone: string;
	lastupdate: number;
	history: WorkerHistory[];
	availableStatuses: WorkerMissionStatus[];
	alerts?: NeedRecruitmentCardAlerts[];
}

export interface WorkerHistory {
	status: WorkerMissionStatus;
	date: number;
	reason?: string;
}

export enum InitialTabType {
	details = 'details',
	propositions = 'propositions',
}

export interface MissionsDetailModalProps {
	missionId: string;
	open: boolean;
	initialTab: InitialTabType;
}

export interface MissionBulkWorker {
	workerId: string;
	firstname: string;
	lastname: string;
	email: string;
	phone: string;
	fromJobOffer?: boolean;
}

export enum MissionBulkWorkerStatus {
	Proposed = 'PROPOSED',
	Confirmed = 'CONFIRMED',
}

export interface AddWorkersToMissionBulkRequest {
	status: MissionBulkWorkerStatus;
	workers: MissionBulkWorker[];
}

export const MISSION_WORKER_ALREADY_ASSIGNED_CODE =
	'MISSION.WORKER_ALREADY_ASSIGNED';
export const MISSION_MAX_WORKERS_REACHED_CODE = 'MISSION.MAX_WORKERS_REACHED';
export const MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE =
	'MISSION.WORKER_REGISTRATION_NOT_COMPLETE';
export const MISSION_WORKER_SCHEDULE_CONFLICT_CODE =
	'MISSION.WORKER_SCHEDULE_CONFLICT';

export interface AddWorkersToMissionBulkStatusItem {
	success: boolean;
	workerId: string;
	error?: string;
	code?: string;
}

export interface AddWorkersToMissionAndMoveProfilesToMissionProposedParams {
	missionId: string;
	companyNeedId: string;
	workers: MissionBulkWorker[];
	continueIfMissionUpdateFails?: boolean;
}

export interface AddWorkersToMissionAndMoveProfilesToMissionProposedResult {
	isMissionUpdateSuccessful: boolean;
	alreadyAssignedCount: number;
}

export interface UpdateMissionWorkerStatus {
	status: WorkerMissionStatus;
	reason?: string;
}
export interface UpdateMissionWorkerStatusBulk {
	status: WorkerMissionStatus;
	workerIds: string[];
}

export interface CancelMissionData {
	cancelReason: CancelReason;
	cancelExplanation?: string;
}

export enum CancelNotificationWarning {
	NOT_STARTED = 'notStarted',
	IN_PROGRESS = 'inProgress',
	ENDED = 'ended',
}

export interface ExtendMissionPayload {
	besoinId: string;
	companyId: string;
	jobId: string;
	contractType: string;
	totalPositions: number;
	periods: Period[];
	address: OrderAddress;
	name?: string;
	hiringReason?: string;
	justification?: string;
	description?: string;
	note?: string;
	workerIds?: string[];
}
